import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { removeAppointmentsBookingCartTurn } from '../../../actions'
import { ChevronDown, Close } from '../../../assets/icons'
import { Button } from '../../ui/button'
import { Text1, Text2 } from '../../ui/text'
import { capitalizeEachWord } from '../../../utils/stringsUtils'
import useIsMobile from '../../../hooks/useIsMobile'
import moment from 'moment'
import colors from '../../../styles/colors'

const AppointmentCart = ({
	cartTurns = [],
	removeAppointmentsBookingCartTurn,
	onContinue,
	disabled = false
}) => {
	const isMobile = useIsMobile()
	const [isExpanded, setIsExpanded] = useState(!isMobile)

	if (!cartTurns || cartTurns.length === 0) {
		return null
	}

	const handleRemove = (idTurno, e) => {
		e.stopPropagation()
		removeAppointmentsBookingCartTurn(idTurno)
	}

	const cartContent = (
		<Container isMobile={isMobile}>
			<Header onClick={() => isMobile && setIsExpanded(!isExpanded)} isMobile={isMobile}>
				<HeaderLeft>
					<Title>Turnos a reservar</Title>
					<Count>{cartTurns.length} seleccionado{cartTurns.length > 1 ? 's' : ''}</Count>
				</HeaderLeft>
				{!isMobile && (
					<HeaderActions>
						<Button
							label="Continuar"
							$variant="primary"
							onClick={onContinue}
							disabled={disabled || cartTurns.length === 0}
							size="tiny"
						/>
						<ChevronToggle
							type="button"
							onClick={() => setIsExpanded(!isExpanded)}
							aria-label={isExpanded ? 'Contraer turnos' : 'Expandir turnos'}
						>
							<ChevronIcon $isExpanded={isExpanded}>
								<ChevronDown size={16} />
							</ChevronIcon>
						</ChevronToggle>
					</HeaderActions>
				)}
				{isMobile && (
					<ChevronIcon $isExpanded={isExpanded}>
						<ChevronDown size={16} />
					</ChevronIcon>
				)}
			</Header>

			{isExpanded && (
				<>
					<TurnosList>
						{cartTurns.map((turn, idx) => (
							<TurnoItem key={`${turn.idTurno}-${idx}`}>
								<TurnoInfo>
									<TurnoName>{capitalizeEachWord(turn.nombreProfesional)}</TurnoName>
									<TurnoDetails>
										{capitalizeEachWord(turn.nombreEspecialidad)} - {moment(turn.fechaTurno, 'HH:mm DD/MM/YYYY').format('DD/MM/YYYY')}  - {moment(turn.fechaTurnoDateTime).format('HH:mm')}									</TurnoDetails>
								</TurnoInfo>
								<RemoveButton
									onClick={e => handleRemove(turn.idTurno, e)}
									type="button"
									aria-label="Eliminar turno"
								>
									<Close size={20} />
								</RemoveButton>
							</TurnoItem>
						))}
					</TurnosList>
					{isMobile && (
						<FooterActions>
							<Button
								label="Continuar"
								$variant="primary"
								onClick={onContinue}
								disabled={disabled || cartTurns.length === 0}
								size="mobile"
								style={{ width: '100%' }}
							/>
						</FooterActions>
					)}
				</>
			)}
		</Container>
	)

	if (isMobile) {
		return cartContent
	}

	return ReactDOM.createPortal(cartContent, document.body)
}

const Container = styled.div`
	padding: 1.25vw 2vw;
	position: fixed;
	bottom: 0px;
	left: 0vw;
	right: 0;
	background: white;
	width: auto;
	box-sizing: border-box;
	z-index: 100;
  box-shadow: 0 -2px 6px rgba(130, 130, 130, 0.15);

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		padding: 12px;
		position: fixed;
		bottom: 0px;
		border-top: 1px solid ${props => props.theme.colors.greys.dividerGray};
		width: 100vw;
		left: 0px;
	}
`

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: ${props => props.isMobile && props.onClick ? 'pointer' : 'default'};
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		user-select: none;
		padding: 8px 0;
	}
`

const HeaderLeft = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-right: 8px;
	flex-shrink: 0;
	justify-content: flex-end;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		margin-right: 0;
	}
`

const Title = styled(Text1)`
font-size: 16px;
	font-weight: 600;
	color: ${colors.text.titleText};
	margin: 0;
`

const Count = styled(Text2)`
	font-size: 14px;
	color: ${colors.text.mutedText};
	margin: 0;
`

const ChevronIcon = styled.div`
	display: flex;
	align-items: center;
	color: ${colors.text.mutedText};
	transition: transform 0.2s ease;
	transform: ${props => (props.$isExpanded ? 'rotate(0deg)' : 'rotate(180deg)')};
`

const ChevronToggle = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
`

const TurnosList = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
	margin-top: 16px;
	max-height: 300px;
	overflow-y: auto;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		gap: 10px;
		margin-top: 12px;
		margin-bottom: 12px;
		flex-direction: column;
	}
`

const TurnoItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: ${props => props.theme.colors.greys.hoverBg};
	padding: 0.62vw;
	gap: 0.62vw;
	border-radius: 4px;
	width: fit-content;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 100%;
		padding: 10px;
	}
`

const TurnoInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: row;
	gap: 8px;
	align-items: center
`

const TurnoName = styled(Text1)`
	font-weight: 600;
	font-size: 14px;
	color: ${colors.text.titleText};
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		font-size: 14px;
	}
`

const TurnoDetails = styled(Text2)`
	font-size: 14px;
	color: ${colors.text.mutedText};
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		font-size: 11px;
	}
`

const RemoveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	min-width: 32px;
	background: transparent;
	border: none;
	cursor: pointer;
	color: ${colors.text.mutedText};
	padding: 0;
	border-radius: 4px;
	transition: all 0.2s ease;

	&:hover {
		background: ${colors.greys.lightGrey};
		color: ${colors.brand.blueOmint};
	}

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 28px;
		height: 28px;
		min-width: 28px;
	}
`

const FooterActions = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 12px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		flex-direction: column;
		gap: 8px;
	}
`

const mapStateToProps = ({ appointment }) => ({
	cartTurns: appointment.booking.cartTurns
})

const mapDispatchToProps = {
	removeAppointmentsBookingCartTurn
}

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentCart)
